import { create } from "zustand";
import { applyAssetResolution, buildAssetUrl } from "../pixi/assetsInit";

export interface SpineAsset {
  skeleton: string;
  atlas: string;
}

export interface SkinPreset {
  id: number;
  name: string;
  description: string;
  preview: string;
  bg: SpineAsset;
  space?: SpineAsset;
  bg2?: SpineAsset;
  pioneer?: SpineAsset;
  capitalist?: SpineAsset;
  stairs?: SpineAsset;
  snow?: SpineAsset;
  twins?: SpineAsset;
}

const IS_LOCAL_SPINE = import.meta.env.VITE_LOCAL_SPINE === "true";

const spineAsset = (skeleton: string, atlas: string): SpineAsset => {
  if (IS_LOCAL_SPINE) {
    // Local: /spine/ без webp атласов
    const localAtlas = atlas.replace(".webp.atlas", ".atlas");
    return {
      skeleton: `/spine/${skeleton}`,
      atlas: `/spine/${localAtlas}`,
    };
  }
  return {
    skeleton: `/spineOptimized/${skeleton}`,
    atlas: `/spineOptimized/${atlas}`,
  };
};

export const resolveSpineAsset = (asset: SpineAsset): SpineAsset => {
  if (IS_LOCAL_SPINE) {
    return { skeleton: asset.skeleton, atlas: asset.atlas };
  }
  // Skeleton (.json) одинаков для всех mip-уровней, а atlas / png
  // имеют свой суффикс (@0.5x / @0.25x), который AssetPack создаёт.
  return {
    skeleton: buildAssetUrl(asset.skeleton),
    atlas: buildAssetUrl(applyAssetResolution(asset.atlas)),
  };
};

/** Все spine-слои пресета (для предзагрузки «других» скинов теми же URL, что и хуки). */
export function collectSpineAssetsFromPreset(preset: SkinPreset): SpineAsset[] {
  const parts: (SpineAsset | undefined)[] = [
    preset.bg,
    preset.space,
    preset.bg2,
    preset.pioneer,
    preset.capitalist,
    preset.stairs,
    preset.snow,
    preset.twins,
  ];
  return parts.filter((x): x is SpineAsset => !!x);
}

const stripExt = (path: string): string => path.replace(/\.[^/.]+$/, "");

export const resolvePreviewUrls = (
  preview: string
): { webp: string; png: string } => {
  const base = `/asset_baseOptimized/${stripExt(preview)}`;
  return {
    webp: buildAssetUrl(applyAssetResolution(`${base}.webp`)),
    png: buildAssetUrl(applyAssetResolution(`${base}.png`)),
  };
};

const SKINS: Record<GameType, SkinPreset[]> = {
  pioneer: [
    {
      id: 0,
      name: "Default",
      description: "default layout",
      preview: "skins/pioneer/base_bg.png",
      bg: spineAsset(
        "pioneer-game/bg/default/background.json",
        "pioneer-game/bg/default/background.webp.atlas"
      ),
      space: spineAsset(
        "pioneer-game/bgSpace/default/space.json",
        "pioneer-game/bgSpace/default/space.webp.atlas"
      ),
      pioneer: spineAsset(
        "pioneer-game/pioneer/default/boy.json",
        "pioneer-game/pioneer/default/boy.webp.atlas"
      ),
      capitalist: spineAsset(
        "pioneer-game/capitalist/default/Capitalist.json",
        "pioneer-game/capitalist/default/Capitalist.webp.atlas"
      ),
      stairs: spineAsset(
        "pioneer-game/stairs/default/stairs.json",
        "pioneer-game/stairs/default/stairs.webp.atlas"
      ),
    },
    {
      id: 1,
      name: "Winter",
      description: "winter winter winter",
      preview: "skins/pioneer/winter_bg.png",
      bg: spineAsset(
        "pioneer-game/bg/winter/background.json",
        "pioneer-game/bg/winter/background.webp.atlas"
      ),
      space: spineAsset(
        "pioneer-game/bgSpace/winter/space.json",
        "pioneer-game/bgSpace/winter/space.webp.atlas"
      ),
      pioneer: spineAsset(
        "pioneer-game/pioneer/winter/boy.json",
        "pioneer-game/pioneer/winter/boy.webp.atlas"
      ),
      capitalist: spineAsset(
        "pioneer-game/capitalist/winter/Capitalist.json",
        "pioneer-game/capitalist/winter/Capitalist.webp.atlas"
      ),
      stairs: spineAsset(
        "pioneer-game/stairs/winter/stairs.json",
        "pioneer-game/stairs/winter/stairs.webp.atlas"
      ),
    },
  ],
  twins: [
    {
      id: 0,
      name: "Default",
      description: "",
      preview: "skins/twins/base_bg.png",
      bg: spineAsset(
        "twins-game/bg/default/background.json",
        "twins-game/bg/default/background.webp.atlas"
      ),
      bg2: spineAsset(
        "twins-game/bg2/default/back_02.json",
        "twins-game/bg2/default/back_02.webp.atlas"
      ),
      twins: spineAsset(
        "twins-game/twins/default/twin.json",
        "twins-game/twins/default/twin.webp.atlas"
      ),
    },
    {
      id: 1,
      name: "Winter",
      description: "winter theme",
      preview: "skins/twins/winter_bg.png",
      bg: spineAsset(
        "twins-game/bg/winter/background.json",
        "twins-game/bg/winter/background.webp.atlas"
      ),
      bg2: spineAsset(
        "twins-game/bg2/winter/back_02.json",
        "twins-game/bg2/winter/back_02.webp.atlas"
      ),
      snow: spineAsset(
        "twins-game/snow/object.json",
        "twins-game/snow/object.webp.atlas"
      ),
      twins: spineAsset(
        "twins-game/twins/winter/twin.json",
        "twins-game/twins/winter/twin.webp.atlas"
      ),
    },
  ],
};

const STORAGE_KEY = "game_skin_selection";

const loadFromStorage = (): Record<GameType, number> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.warn("Failed to load skin settings", e);
  }
  return { pioneer: 1, twins: 1 };
};

const saveToStorage = (data: Record<GameType, number>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn("Failed to save skin settings", e);
  }
};

interface SkinStoreState {
  skinIds: Record<GameType, number>;
  isSkinChanging: boolean;
  getSkin: (gameType: GameType) => SkinPreset;
  setSkin: (gameType: GameType, skinId: number) => void;
  getAvailableSkins: (gameType: GameType) => SkinPreset[];
}

export const useSkinStore = create<SkinStoreState>((set, get) => ({
  skinIds: loadFromStorage(),
  isSkinChanging: false,

  getSkin: (gameType) => {
    const skinId = get().skinIds[gameType];
    return (
      SKINS[gameType].find((skin) => skin.id === skinId) || SKINS[gameType][0]
    );
  },

  setSkin: (gameType, skinId) => {
    const skinExists = SKINS[gameType].some((skin) => skin.id === skinId);
    if (!skinExists) return;

    const currentSkinId = get().skinIds[gameType];
    if (currentSkinId === skinId) return;

    set({ isSkinChanging: true });

    const newSkinIds = { ...get().skinIds, [gameType]: skinId };
    set({ skinIds: newSkinIds });
    saveToStorage(newSkinIds);

    setTimeout(() => {
      set({ isSkinChanging: false });
    }, 2000);
  },

  getAvailableSkins: (gameType) => SKINS[gameType],
}));
